import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

/**
 * Correct a live indent, and walk back what was corrected.
 *
 * Distinct from the approval editor, which fixes a DRAFT before an indent
 * exists. This fixes one that is already live, so it is restricted to a
 * procurement admin or the configured gatekeeper and every field change is
 * recorded with its before and after.
 *
 * The history is append-only: a revert is a NEW entry applying the inverse,
 * never the deletion of the entry that recorded the original change.
 */

export type IndentEditEntry = {
  at: string;
  byName: string;
  byEmail?: string;
  note?: string;
  revertOf?: string;
  changes: Array<{ field: string; label: string; from: string; to: string }>;
};

type FieldDef = { key: string; label: string; type: 'text' | 'number' | 'date' };

const fmt = (d: string) => new Date(d).toLocaleString('en-IN');

export default function IndentEditPanel({
  indent, editLog,
}: {
  indent: Record<string, any>;
  editLog?: IndentEditEntry[];
}) {
  const qc = useQueryClient();
  const indentId = String(indent?.indentId || '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState<Record<string, any>>({});

  const { data: perm } = useQuery<{ allowed: boolean; isAdmin: boolean }>({
    queryKey: ['procurement', 'indent-lifecycle-perm'],
    queryFn: () => api.get('/api/procurement/indents/lifecycle/can-control'),
    staleTime: 5 * 60_000,
  });
  const { data: fieldData } = useQuery<{ fields: FieldDef[] }>({
    queryKey: ['procurement', 'indent-editable-fields'],
    queryFn: () => api.get('/api/procurement/indents/lifecycle/editable-fields'),
    staleTime: 60 * 60_000,
    enabled: !!perm?.allowed,
  });
  const fields = fieldData?.fields ?? [];

  const startEditing = () => {
    const d: Record<string, any> = {};
    for (const f of fields) d[f.key] = indent?.[f.key] ?? '';
    setDraft(d);
    setNote('');
    setOpen(true);
  };

  const changed = useMemo(
    () => fields.filter((f) => String(draft[f.key] ?? '') !== String(indent?.[f.key] ?? '')),
    [fields, draft, indent],
  );

  const save = async () => {
    if (!changed.length) return;
    setBusy(true);
    try {
      const patch = Object.fromEntries(changed.map((f) => [f.key, draft[f.key]]));
      const r = await api.post<{ changed: number }>(
        `/api/procurement/indents/${encodeURIComponent(indentId)}/edit`, { patch, note: note.trim() });
      toast.success(`${r.changed} field${r.changed === 1 ? '' : 's'} updated`);
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the changes');
    } finally { setBusy(false); }
  };

  const revert = async (i: number) => {
    if (!window.confirm('Undo this change? It is applied as a new entry — the original record stays in the history.')) return;
    setBusy(true);
    try {
      const r = await api.post<{ reverted: number }>(
        `/api/procurement/indents/${encodeURIComponent(indentId)}/edit/${i}/revert`, {});
      toast.success(r.reverted ? `${r.reverted} field${r.reverted === 1 ? '' : 's'} put back` : 'Nothing needed reverting');
      await qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
    } catch (e: any) {
      toast.error(e?.message || 'Could not revert');
    } finally { setBusy(false); }
  };

  const log = editLog ?? [];
  // The history is worth showing to anyone who opens the indent; only the
  // gatekeeper gets the buttons.
  if (!perm?.allowed && !log.length) return null;

  return (
    <div className="space-y-3">
      {perm?.allowed && !open && (
        <button onClick={startEditing} disabled={busy}
          className="press rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          Edit indent details
        </button>
      )}

      {open && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-800">Editing {indentId}</div>
          <p className="mb-3 text-[11.5px] text-amber-900">
            Every change is recorded against your name with its before and after, and can be undone from the
            history below. The pipeline status and hold/cancel state are not editable here — they have their own flows.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {fields.map((f) => {
              const dirty = String(draft[f.key] ?? '') !== String(indent?.[f.key] ?? '');
              return (
                <div key={f.key}>
                  <label className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    {f.label}{dirty && <span className="ml-1 text-amber-700">changed</span>}
                  </label>
                  <input
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                    className={'input w-full' + (dirty ? ' border-amber-400 bg-white' : '')}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft((d) => ({
                      ...d,
                      [f.key]: f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                    }))}
                  />
                  {dirty && (
                    <div className="mt-0.5 text-[10.5px] text-slate-500">
                      was <span className="font-mono">{String(indent?.[f.key] ?? '') || '—'}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <label className="mb-0.5 mt-3 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Why</label>
          <input className="input w-full" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. quantity corrected against the client PO" />
          <div className="mt-3 flex items-center justify-end gap-2">
            <span className="mr-auto text-[11.5px] text-amber-900">
              {changed.length ? `${changed.length} field${changed.length === 1 ? '' : 's'} changed` : 'No changes yet'}
            </span>
            <button disabled={busy} onClick={() => setOpen(false)}
              className="press rounded-lg border border-slate-300 bg-white px-4 py-1.5 text-[12px] font-bold text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button disabled={busy || !changed.length} onClick={save}
              className="press rounded-lg bg-amber-600 px-5 py-1.5 text-[12px] font-bold text-white hover:bg-amber-700 disabled:opacity-50">
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Change history · {log.length} edit{log.length === 1 ? '' : 's'}
          </div>
          <ul className="space-y-2">
            {log.map((e, i) => (
              <li key={i} className={'rounded-lg border px-2.5 py-2 ' + (e.revertOf ? 'border-slate-200 bg-slate-50' : 'border-slate-100')}>
                <div className="flex flex-wrap items-center gap-x-2 text-[11.5px] text-slate-500">
                  <b className="text-slate-700">{e.byName || e.byEmail || 'Unknown'}</b>
                  <span>{fmt(e.at)}</span>
                  {e.revertOf && (
                    <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">revert</span>
                  )}
                  {perm?.allowed && !e.revertOf && (
                    <button disabled={busy} onClick={() => revert(i)}
                      className="ml-auto rounded border border-slate-300 bg-white px-2 py-0.5 text-[10.5px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                      Undo this
                    </button>
                  )}
                </div>
                {e.note && <div className="mt-0.5 text-[11.5px] italic text-slate-500">{e.note}</div>}
                <ul className="mt-1 space-y-0.5">
                  {e.changes.map((c, j) => (
                    <li key={j} className="text-[11px]">
                      <span className="font-semibold text-slate-700">{c.label}</span>{': '}
                      <span className="font-mono text-rose-600 line-through">{c.from || '—'}</span>
                      {' → '}
                      <span className="font-mono font-bold text-emerald-700">{c.to || '—'}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
